import { useState, useEffect, useCallback, useRef } from "react";
import { Modal, InputNumber, Tooltip } from "antd";
import { useOrderingMessage } from "../../useOrderingMessage";
import dayjs, { type Dayjs } from "dayjs";
import {
  FileTextOutlined,
  CalendarOutlined,
  CheckOutlined,
  PlusCircleOutlined,
  MinusCircleOutlined,
  PrinterOutlined,
  ToolOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { getApiErrorMessage } from "../../../../../utils/translations";
import { posModalStyles as modalStyles } from "../../modalPrimitives";
import {
  useXReport,
  useZReport,
  useFmReport,
  useCashIn,
  useCashOut,
} from "../../../../../hooks/useOperations";
import { useViewport } from "../../../../../hooks/useViewport";
import { usePrinterNotifications } from "../../../../../hooks/usePrinterNotifications";
import { useIsEdgeLocal } from "../../../../../hooks/useEdgeMode";
import type {
  FiscalOperationResponse,
  FmReportDetail,
} from "../../../../../api/operations";
import { FmReportFields } from "./FmReportFields";
import {
  buildFmPeriod,
  defaultFmPreset,
  fmPresetRange,
  isLongFmReport,
  type FmPeriod,
  type FmReportPreset,
} from "./fmReportPeriod";
import { ConfirmActionModal } from "../ConfirmActionModal";
import { R } from "../../orderingTheme";
import {
  ModalHeader,
  ModalFooter,
  GhostButton,
  PrimaryButton,
} from "../../modalPrimitives";

interface OperationsModalProps {
  open: boolean;
  onClose: () => void;
}

const OPTIONS = ["x", "z", "fmReport", "cashIn", "cashOut"] as const;
type OptionKey = (typeof OPTIONS)[number];

const isCashOption = (option: OptionKey): boolean =>
  option === "cashIn" || option === "cashOut";

export const OperationsModal = ({ open, onClose }: OperationsModalProps) => {
  const { t } = useTranslation();
  const message = useOrderingMessage();
  const { notifyPrinterError, notifyPrinterTimeout } = usePrinterNotifications();
  const { isMobile, isNarrow, isTablet, isDesktop } = useViewport();
  const [selected, setSelected] = useState<OptionKey>("x");
  const [amount, setAmount] = useState<number | null>(null);
  const [fmDetail, setFmDetail] = useState<FmReportDetail>("short");
  const [fmPreset, setFmPreset] = useState<FmReportPreset>(() =>
    defaultFmPreset(),
  );
  // Календарите на „Период" тръгват от границите на пресета по подразбиране.
  const [fmFrom, setFmFrom] = useState<Dayjs | null>(
    () => fmPresetRange(defaultFmPreset()).start,
  );
  const [fmTo, setFmTo] = useState<Dayjs | null>(
    () => fmPresetRange(defaultFmPreset()).end,
  );
  // Дълъг подробен отчет, който чака изричното „Печатай" на оператора.
  const [longPeriodPending, setLongPeriodPending] = useState<FmPeriod | null>(
    null,
  );
  const amountInputRef = useRef<HTMLInputElement>(null);

  const xReportMutation = useXReport();
  const zReportMutation = useZReport();
  const fmReportMutation = useFmReport();
  const cashInMutation = useCashIn();
  const cashOutMutation = useCashOut();
  // Локален режим (docs/edge.md): X/Z и служебният внос/износ минават през
  // касата, но отчетът от фискалната памет (fm-report) остава извън обхвата —
  // редът стои, disabled с подсказка, стрелките го прескачат.
  const edgeLocal = useIsEdgeLocal();
  const isLockedOption = useCallback(
    (option: OptionKey): boolean => option === "fmReport" && edgeLocal,
    [edgeLocal],
  );

  useEffect(() => {
    if (open) {
      setSelected("x");
      setAmount(null);
      setFmDetail("short");
      const preset = defaultFmPreset();
      const presetRange = fmPresetRange(preset);
      setFmPreset(preset);
      setFmFrom(presetRange.start);
      setFmTo(presetRange.end);
      setLongPeriodPending(null);
    }
  }, [open]);

  useEffect(() => {
    if (isCashOption(selected)) {
      setTimeout(() => amountInputRef.current?.focus(), 100);
    }
  }, [selected]);

  // Локализира {bg,en} detail-а от BE; иначе — общото „Грешка при операция"
  const extractError = useCallback(
    (error: unknown): string => getApiErrorMessage(error, t("operations.error")),
    [t],
  );

  // Общ изход на 200-отговор с провал от принтера: timeout → жълто
  // предупреждение (изходът е неизвестен, отчетът МОЖЕ и да е излязъл),
  // failed → червена грешка. Връща true, когато операцията трябва да спре.
  const notifyIfPrinterFailed = useCallback(
    (result: FiscalOperationResponse): boolean => {
      if (result.status === "timeout") {
        notifyPrinterTimeout(result.error || t("operations.error"));
        return true;
      }
      if (result.status === "failed") {
        notifyPrinterError(result.error || t("operations.error"));
        return true;
      }
      return false;
    },
    [notifyPrinterError, notifyPrinterTimeout, t],
  );

  // И HTTP отказът тук е принтерен (endpoint-ите са чисто фискални,
  // напр. „Не е намерен фискален принтер...") — стои до ръчно затваряне.
  const reportOperationError = useCallback(
    (error: unknown) => {
      notifyPrinterError(extractError(error));
      console.error("Operations error:", error);
    },
    [notifyPrinterError, extractError],
  );

  // Периодът на отчета от ФП: бързите пресети имат фиксирани граници, а
  // „Период" минава през проверките (празен, бъдещ, обърнат). null = спряно
  // с предупреждение, заявка не тръгва.
  const resolveFmPeriod = useCallback((): FmPeriod | null => {
    if (fmPreset !== "period") return fmPresetRange(fmPreset);
    if (!fmFrom || !fmTo) {
      message.warning(t("operations.fmReportPeriodRequired"));
      return null;
    }
    const today = dayjs().endOf("day");
    const start = fmFrom.startOf("day");
    let end = fmTo.endOf("day");
    if (start.isAfter(today)) {
      message.warning(t("operations.fmReportFuturePeriod"));
      return null;
    }
    if (end.isBefore(start)) {
      message.warning(t("operations.fmReportInvalidPeriod"));
      return null;
    }
    // Календарите не дават бъдещи дати, но периодът може да е зареден
    // от по-стар ден — гейтуеят и сам реже до днес.
    if (end.isAfter(today)) end = today;
    return buildFmPeriod(start, end);
  }, [fmPreset, fmFrom, fmTo, message, t]);

  const printFmReport = useCallback(
    async (period: FmPeriod) => {
      try {
        const result = await fmReportMutation.mutateAsync({
          detail: fmDetail,
          startDate: period.start.format("YYYY-MM-DD"),
          endDate: period.end.format("YYYY-MM-DD"),
        });
        if (notifyIfPrinterFailed(result)) return;
        message.success(t("operations.fmReportSuccess"));
        onClose();
      } catch (error) {
        reportOperationError(error);
      }
    },
    [
      fmDetail,
      fmReportMutation,
      notifyIfPrinterFailed,
      message,
      t,
      onClose,
      reportOperationError,
    ],
  );

  // Потвърденият дълъг отчет: затваря въпроса и печата.
  const confirmLongPeriod = useCallback(() => {
    const period = longPeriodPending;
    setLongPeriodPending(null);
    if (period) void printFmReport(period);
  }, [longPeriodPending, printFmReport]);

  const handleConfirm = useCallback(async () => {
    if (isLockedOption(selected)) return;
    if (selected === "fmReport") {
      const period = resolveFmPeriod();
      if (!period) return;
      // Дълъг подробен отчет НЕ се спира — операторът потвърждава, че иска
      // толкова хартия (съкратеният само сумира ФП и няма праг).
      if (isLongFmReport(fmDetail, period)) {
        setLongPeriodPending(period);
        return;
      }
      await printFmReport(period);
      return;
    }
    try {
      if (selected === "x") {
        const result = await xReportMutation.mutateAsync();
        if (notifyIfPrinterFailed(result)) return;
        message.success(t("operations.xReportSuccess"));
      } else if (selected === "z") {
        const result = await zReportMutation.mutateAsync();
        if (notifyIfPrinterFailed(result)) return;
        message.success(t("operations.zReportSuccess"));
      } else if (selected === "cashIn" || selected === "cashOut") {
        if (!amount || amount <= 0) {
          message.warning(t("operations.amountRequired"));
          amountInputRef.current?.focus();
          return;
        }

        if (selected === "cashIn") {
          const result = await cashInMutation.mutateAsync({ amount });
          if (notifyIfPrinterFailed(result)) return;
          message.success(t("operations.cashInSuccess"));
        } else {
          const result = await cashOutMutation.mutateAsync({ amount });
          if (notifyIfPrinterFailed(result)) return;
          message.success(t("operations.cashOutSuccess"));
        }
      }
      onClose();
    } catch (error) {
      reportOperationError(error);
    }
  }, [
    selected,
    amount,
    fmDetail,
    isLockedOption,
    resolveFmPeriod,
    printFmReport,
    xReportMutation,
    zReportMutation,
    cashInMutation,
    cashOutMutation,
    onClose,
    t,
    message,
    notifyIfPrinterFailed,
    reportOperationError,
  ]);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Отворен въпрос за дълъг отчет: Enter печата, Esc го отказва — изборът
      // отдолу не се пипа.
      if (longPeriodPending) {
        if (e.key === "Enter") {
          e.preventDefault();
          confirmLongPeriod();
        } else if (e.key === "Escape") {
          e.preventDefault();
          setLongPeriodPending(null);
        }
        return;
      }
      // Календарът на периода и превключвателят имат свои клавиши: стрелки
      // местят датата, Enter потвърждава деня, Esc затваря календара — не
      // модала. Сумата (InputNumber) нарочно НЕ е тук: Enter в нея потвърждава.
      const target = e.target as HTMLElement | null;
      if (target?.closest?.(".ant-picker, .ant-picker-dropdown, .ant-segmented")) {
        return;
      }
      if (e.key === "ArrowUp" || e.key === "ArrowDown") {
        e.preventDefault();
        setSelected((prev) => {
          const options = OPTIONS.filter((option) => !isLockedOption(option));
          const currentIndex = Math.max(0, options.indexOf(prev));
          const nextIndex =
            e.key === "ArrowDown"
              ? (currentIndex + 1) % options.length
              : (currentIndex - 1 + options.length) % options.length;
          return options[nextIndex];
        });
      } else if (e.key === "Enter") {
        e.preventDefault();
        handleConfirm();
      } else if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    open,
    handleConfirm,
    onClose,
    longPeriodPending,
    confirmLongPeriod,
    isLockedOption,
  ]);

  const isLoading =
    xReportMutation.isPending ||
    zReportMutation.isPending ||
    fmReportMutation.isPending ||
    cashInMutation.isPending ||
    cashOutMutation.isPending;

  const getOptionConfig = (option: OptionKey) => {
    switch (option) {
      case "x":
        return { label: t("operations.xReport"), icon: <FileTextOutlined /> };
      case "z":
        return { label: t("operations.zReport"), icon: <FileTextOutlined /> };
      case "fmReport":
        return {
          label: t("operations.fmReport"),
          icon: <CalendarOutlined />,
        };
      case "cashIn":
        return { label: t("operations.cashIn"), icon: <PlusCircleOutlined /> };
      case "cashOut":
        return {
          label: t("operations.cashOut"),
          icon: <MinusCircleOutlined />,
        };
    }
  };

  const width = isMobile ? undefined : isTablet ? 440 : 480;
  const bodyPad = isNarrow ? "16px" : "20px 24px";

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      closable={false}
      destroyOnHidden
      width={width}
      styles={modalStyles}
      style={{ maxWidth: "calc(100vw - 32px)" }}
    >
      <div
        style={{ background: R.surface, fontFamily: R.font, color: R.text }}
      >
        <ModalHeader
          icon={<ToolOutlined />}
          title={t("operations.selectType")}
          onClose={onClose}
          isNarrow={isNarrow}
        />

        <div
          style={{
            padding: bodyPad,
            display: "flex",
            flexDirection: "column",
            gap: 12,
          }}
        >
          <div
            style={{ display: "flex", flexDirection: "column", gap: 8 }}
          >
            {OPTIONS.map((option) => {
              const isSelected = selected === option;
              const locked = isLockedOption(option);
              const inactive = isLoading || locked;
              const { label, icon } = getOptionConfig(option);
              const optionButton = (
                <button
                  type="button"
                  onClick={() => {
                    setSelected(option);
                    if (!isCashOption(option)) setAmount(null);
                  }}
                  disabled={inactive}
                  // Заключен ред: подсказката е на обвиващия span (disabled
                  // бутон не получава mouse събития).
                  className={locked ? "pointer-events-none" : undefined}
                  style={{
                    padding: "12px 14px",
                    borderRadius: 12,
                    border: `1px solid ${
                      inactive
                        ? R.border
                        : isSelected
                          ? "color-mix(in srgb, var(--color-brand) 32%, transparent)"
                          : R.border
                    }`,
                    background: inactive
                      ? "var(--color-shade-1)"
                      : isSelected
                        ? R.brandTint
                        : R.surface,
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    cursor: inactive ? "not-allowed" : "pointer",
                    opacity: inactive ? 0.6 : 1,
                    filter: inactive ? "grayscale(0.4)" : "none",
                    fontFamily: "inherit",
                    textAlign: "left",
                    transition: "background 120ms, border-color 120ms",
                  }}
                >
                  <span
                    style={{
                      width: 38,
                      height: 38,
                      borderRadius: 10,
                      background: isSelected ? R.brand : R.bg,
                      color: isSelected ? "#FFFFFF" : R.textSoft,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 17,
                      flexShrink: 0,
                    }}
                  >
                    {icon}
                  </span>
                  <span
                    style={{
                      flex: 1,
                      fontSize: 14.5,
                      fontWeight: 600,
                      color: isSelected ? R.brandDeep : R.text,
                    }}
                  >
                    {label}
                  </span>
                  <span
                    style={{
                      width: 20,
                      height: 20,
                      borderRadius: 10,
                      background: isSelected ? R.brand : R.surface,
                      border: `1px solid ${
                        isSelected ? R.brand : R.borderMed
                      }`,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#FFFFFF",
                      flexShrink: 0,
                    }}
                  >
                    {isSelected && <CheckOutlined style={{ fontSize: 11 }} />}
                  </span>
                </button>
              );
              return (
                <div
                  key={option}
                  style={{ display: "flex", flexDirection: "column", gap: 10 }}
                >
                {locked ? (
                  <Tooltip title={t("edge.unavailableOffline")}>
                    <span className="grid cursor-not-allowed">
                      {optionButton}
                    </span>
                  </Tooltip>
                ) : (
                  optionButton
                )}
                {option === "fmReport" && isSelected && !locked && (
                  <FmReportFields
                    detail={fmDetail}
                    onDetailChange={setFmDetail}
                    preset={fmPreset}
                    onPresetChange={setFmPreset}
                    from={fmFrom}
                    onFromChange={setFmFrom}
                    to={fmTo}
                    onToChange={setFmTo}
                    disabled={isLoading}
                  />
                )}
                </div>
              );
            })}
          </div>

          {isCashOption(selected) && (
            <div
              style={{ display: "flex", flexDirection: "column", gap: 6 }}
            >
              <div
                style={{
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: 1.2,
                  color: R.textMuted,
                  textTransform: "uppercase",
                }}
              >
                {t("operations.enterAmount")}
              </div>
              <InputNumber
                ref={amountInputRef}
                value={amount}
                onChange={(val) => setAmount(val)}
                min={0.01}
                step={0.01}
                precision={2}
                placeholder="0.00"
                disabled={isLoading}
                keyboard={false}
                onKeyDown={(e) => {
                  if (
                    e.ctrlKey ||
                    e.metaKey ||
                    e.altKey ||
                    e.key.length !== 1
                  )
                    return;
                  if (e.key === ",") {
                    e.preventDefault();
                    const input = e.currentTarget as HTMLInputElement;
                    const start = input.selectionStart ?? input.value.length;
                    const end = input.selectionEnd ?? start;
                    const next =
                      input.value.slice(0, start) + "." + input.value.slice(end);
                    const setter = Object.getOwnPropertyDescriptor(
                      window.HTMLInputElement.prototype,
                      "value",
                    )?.set;
                    setter?.call(input, next);
                    input.dispatchEvent(new Event("input", { bubbles: true }));
                    const pos = start + 1;
                    input.setSelectionRange(pos, pos);
                    return;
                  }
                  if (!/[0-9.]/.test(e.key)) e.preventDefault();
                }}
                style={{
                  width: "100%",
                  height: 44,
                  borderRadius: 10,
                  borderColor: R.border,
                  display: "flex",
                  alignItems: "center",
                }}
              />
            </div>
          )}
        </div>

        <ModalFooter isNarrow={isNarrow} narrowFirst>
          <GhostButton
            label={t("common.cancel")}
            onClick={onClose}
            disabled={isLoading}
            shortcut={isDesktop ? "Esc" : undefined}
          />
          <PrimaryButton
            label={t("common.select")}
            onClick={handleConfirm}
            disabled={
              // Заключен избор (режимът е станал локален, докато модалът е
              // отворен върху „Отчет от фискалната памет") — бутонът е сив,
              // а не мълчалив: handleConfirm така или иначе не го пуска.
              isLockedOption(selected) ||
              (isCashOption(selected) && (!amount || amount <= 0)) ||
              (selected === "fmReport" &&
                fmPreset === "period" &&
                (!fmFrom || !fmTo))
            }
            loading={isLoading}
            shortcut={isDesktop ? "⏎" : undefined}
          />
        </ModalFooter>
      </div>

      <ConfirmActionModal
        open={longPeriodPending !== null}
        icon={<PrinterOutlined />}
        title={t("operations.fmLongPeriodTitle")}
        message={t("operations.fmLongPeriodMessage", {
          days: longPeriodPending?.spanDays ?? 0,
        })}
        question={t("operations.fmLongPeriodQuestion")}
        confirmLabel={t("operations.fmLongPeriodConfirm")}
        onCancel={() => setLongPeriodPending(null)}
        onConfirm={confirmLongPeriod}
      />
    </Modal>
  );
};
